<template>
  <b-row class="internal-mobility-dashboard" v-if="dashboard">
    <b-col cols="12" class="px-4 pt-1">

      <b-row class="my-4 px-4">
        <b-col class="welcome-block bg-primary-a1 px-3">
          <!-- h1 v-if="isSuperAdmin" class="my-4">
            <strong>Welcome back, {{user.firstName}}.</strong>
          </h1 -->
          <h1 class="my-4">
            <strong>Hey there, {{user.firstName}}.</strong>
            <template v-if="dashboard.totalActiveJobs.total">
              <template>We currently have </template>
              <router-link :to="{ name: 'InternalJobIndex' }">{{ dashboard.totalActiveJobs.total | plural('internal opening') }}.</router-link>
            </template>
          </h1>
        </b-col>
      </b-row>

      <b-row class="px-2 mb-4">
        <b-col cols="12" md="8">
          <b-card v-if="dashboard && dashboard.dashboardBlocks" class="h-100">
            <b-row class="pb-3 border-bottom">
              <b-col class="block-title">Our Platform</b-col>
            </b-row>
            <b-row class="pt-3">
              <b-col v-for="block in dashboard.dashboardBlocks" :key="block.name" cols="12" md="6" class="mb-3 pb-2">
                <p class="mb-0"><b>{{ dashboardBlocks.find(({ name }) => name === block.name).displayName }}</b></p>
                <p class="mb-0"><span class="text-muted">{{ dashboardBlocks.find(({ name }) => name === block.name).description }}</span></p>
                <p class="mb-0">
                  <b-link @click="$scrollTo(`#step_${block.name}`)">
                    <b>{{ dashboardBlocks.find(({ name }) => name === block.name).linkText }}</b>
                  </b-link>
                </p>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
        <b-col cols="12" md="4">
          <div v-if="company && company.siteBranding && company.siteBranding.imDashboardHeaderImage">
            <b-img fluid :src="company.siteBranding.imDashboardHeaderImage.fileLocation" />
          </div>
        </b-col>
      </b-row>

      <template v-if="dashboard && dashboard.dashboardBlocks">
        <b-row
          v-for="block in dashboard.dashboardBlocks"
          :key="block.name"
          :id="`step_${block.name}`"
          class="mb-3"
        >
          <b-col cols="12">
            <h3 class="px-2">{{ dashboardBlocks.find(({ name }) => name === block.name).displayName }}</h3>
            <div class="px-2">
              <b-jumbotron class="p-3 mb-3" style="font-size: 15px">
                👉 {{ dashboardBlocks.find(({ name }) => name === block.name).description }}
              </b-jumbotron>
            </div>
            <!-- div v-if="block.name === 'suggested-jobs' && !dashboard.hasMatchedJobs" class="p-2">
              <b-alert show dismissible>It seems like you don't currently have any matched jobs to your profile. See all our openings below.</b-alert>
            </div -->
            <introduce
              v-if="block.name === 'introduce' && dashboard"
              :dashboard="dashboard"
            />
            <content-shares
              v-if="block.name === 'content-sharing' && Array.isArray(dashboard.contentShares)"
              :contentShares="dashboard.contentShares.slice(0, 4)"
            />
            <suggested-jobs
              v-if="block.name === 'suggested-jobs' && Array.isArray(dashboard.jobs)"
              :jobs="dashboard.jobs.slice(0, 3)"
            />
            <suggested-project-jobs
              v-if="block.name === 'suggested-projects' && Array.isArray(dashboard.projectJobs)"
              :jobs="dashboard.projectJobs.slice(0, 3)"
            />
            <suggested-mentors
              v-if="block.name === 'mentorship' && Array.isArray(dashboard.mentors)"
              :mentors="dashboard.mentors" :company="company" :user="user" :count="3"
              @submitContactMentorRequest="submitContactMentorRequest($event)"
            />
          </b-col>
        </b-row>
      </template>
    </b-col>
  </b-row>
</template>

<script>
import base64 from 'base-64';
import enumsMixin from '@src/mixins/enums';
import JobsFilter from '@/components/jobs/FilterContainer';
import SuggestedJobs from '@/components/dashboard/SuggestedJobs.vue';
import SuggestedProjectJobs from '@/components/dashboard/SuggestedProjectJobs.vue';
import SuggestedMentors from '@/components/dashboard/SuggestedMentors.vue';
import ContentShares from '@/components/dashboard/ContentShares.vue';
import Introduce from '@/components/dashboard/Introduce.vue';
import DashboardWelcome from '@/components/dashboard/welcome';
import { mapRoot } from '@src/mixins/root';
import { parseIfPossible, getUserFieldValue } from '@src/lib/imMatching';

// import DashboardRecruiterQuery from '@/graphql/queries/DashboardRecruiter.gql';
import DashboardEmployeeQuery from '@/graphql/queries/DashboardEmployee.gql';
import MentorRequestCreateMutation from '@/graphql/mutations/MentorRequestCreate.gql';

const dashboardBlocks = [
  {
    name: 'introduce',
    displayName: 'Referrals',
    description: 'See your latest referrals and leaderboards',
    linkText: 'Go to Referrals',
  },
  {
    name: 'content-sharing',
    displayName: 'Content Sharing',
    description: 'Earn rewards by sharing great, awesome content!',
    linkText: 'Go to Sharable Content',
  },
  {
    name: 'suggested-jobs',
    displayName: 'Open Jobs',
    description: 'Explore the latest job opportunities. Any openings that match your criteria will be highlighted for you. A job is considered a match when it aligns with your chosen business area, location, and salary range.',
    linkText: 'Go to Your Jobs',
  },
  {
    name: 'suggested-projects',
    displayName: 'Open project roles',
    description: 'Explore the latest project roles. Any roles that match your criteria will be highlighted for you. A project role is considered a match when it aligns with your chosen business area, location, and a skill you have or wish to develop, depending on the role type.',
    linkText: 'Go to Your Projects',
  },
  {
    name: 'mentorship',
    displayName: 'Available Mentors',
    description: 'Explore all the available mentors. Any mentors that match your criteria will be highlighted for you. A mentor is considered a match when they can help you develop a skill you want to improve and provide support in an area you selected during your onboarding.',
    linkText: 'Go to Mentors',
  },
];

const defaultJobSort = (a, b) => {
  if (!a.imMatchingDetails || !b.imMatchingDetails) return 0;
  const { skillScore: skillScoreA, interestScore: interestScoreA } = a.imMatchingDetails || {};
  const { skillScore: skillScoreB, interestScore: interestScoreB } = b.imMatchingDetails || {};
  const scoreA = interestScoreA + skillScoreA * 0.1;
  const scoreB = interestScoreB + skillScoreB * 0.1;
  return -1 * (scoreA - scoreB);
};

const defaultMentorSort = (a, b) => {
  if (!a.imMatchingDetails || !b.imMatchingDetails) return 0;
  return -1 * (a.imMatchingDetails.skillScore - b.imMatchingDetails.skillScore);
};

export default {
  name: 'InternalMobilityDashboard',
  components: {
    SuggestedJobs,
    JobsFilter,
    DashboardWelcome,
    SuggestedProjectJobs,
    ContentShares,
    Introduce,
    SuggestedMentors,
  },
  mixins: [
    enumsMixin,
  ],
  data: () => ({
    filterJobsWhere: {
      status: 'ACTIVE',
    },
    filterJobsOrder: 'CREATED_DESC',
    hasMoreJobsFilter: null,
  }),
  created() {
    if (this.company && this.company.sitePreferences) {
      const { company: { sitePreferences } } = this;
      this.filterJobsOrder = (this.isRecruiter ? sitePreferences.defaultRecruiterJobOrderBy
        : sitePreferences.defaultJobOrderBy) || this.filterJobsOrder;
      if (this.$root && this.$root.isOnlyMentorship) {
        this.$router.replace({ name: 'MentorsIndex' });
      }
    }
    const { company: { customFields: companyCustomFields } = {}, user: { customFields: userCustomFields = [] } = {} } = this;
    if (Array.isArray(companyCustomFields) && Array.isArray(userCustomFields)) {
      const [ interestField ] = companyCustomFields.filter(({ group, type }) => group === 'interest' && type === 'JOB_BUSINESS_AREA');
      const { key } = interestField || {};
      if (key) {
        const userField = userCustomFields.find(el => el.key === key);
        if (!userField || !userField.value) {
          this.$router.push({ name: 'Onboarding', params: { stepName: 'interest' } });
        }
      }
    }
  },
  apollo: {
    dashboard: {
      query: DashboardEmployeeQuery,
      variables() {
        const { sitePreferences: { defaultInternalJobOrderBy } = {} } = this.company;
        return {
          jobOrder: defaultInternalJobOrderBy || 'MOST_RELEVANT_INTEREST',
          projectOrder: defaultInternalJobOrderBy || 'MOST_RELEVANT_INTEREST',
        };
      },
      update({ company, user }) {
        const {
          jobAggs, sharePreferences, shareTemplates,
          totalActiveJobs, dashboardBranding, contentShares,
          mentors: mentorsRaw,
          jobs, projectJobs,
        } = company;
        const {
          matchesCount,
          jobConnections, rewardTokensBalance,
          mentorRequests,
        } = user;
        let mentors = null;
        if (mentorsRaw) {
          jobs.sort(defaultJobSort);
          projectJobs.sort(defaultJobSort);
          mentorsRaw.sort(defaultMentorSort);
          mentors = mentorsRaw.map(el => {
            const mentorRequest = mentorRequests.find(({ mentor: { id: mentorID } }) => mentorID === el.id);
            el.mentorRequest = {};
            if (mentorRequest) {
              el.mentorRequest = mentorRequest;
            }
            return el;
          }).filter(el => {
            const maxMentees = parseIfPossible(getUserFieldValue.call(this, el, 'RANGE', 'mentorship'));
            if (maxMentees && Number.isInteger(maxMentees) && maxMentees > 0 && Array.isArray(el.menteeRequests)) {
              return el.menteeRequests.length < maxMentees;
            }
            return true;
          });
        }
        const matchedJobs = (Array.isArray(this.filterJobs(jobs)) ? this.filterJobs(jobs) : []);
        const matchedProjectJobs = (Array.isArray(this.filterJobs(projectJobs)) ? this.filterJobs(projectJobs) : []);

        return {
          jobAggs,
          totalActiveJobs,
          sharePreferences,
          shareTemplates,
          matchesCount,
          dashboardCard: dashboardBranding ? dashboardBranding.dashboardCard : null,
          jobConnections,
          dashboardBlocks: dashboardBranding && Array.isArray(dashboardBranding.dashboard) ? dashboardBranding.dashboard.filter(({ name }) => {
            return this.$root.isReferralsFeatureEnabled || ![ 'reward-shop', 'introduce' ].includes(name);
          }) : null,
          hasMatchedJobs: matchedJobs.length > 0,
          jobs: [
            ...matchedJobs,
            ...jobs.slice(matchedJobs.length, 3),
          ],
          projectJobs: [
            ...matchedProjectJobs,
            ...projectJobs.slice(matchedProjectJobs.length, 3),
          ],
          contentShares: Array.isArray(contentShares) ? contentShares.filter(({ image, imageURL, description }) => (image || imageURL) && description) : null,
          rewardTokensBalance,
          mentors
        };
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load the dashboard' });
      },
      skip() {
        return !this.isLoggedIn;
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  },
  metaInfo() {
    return {
      title: this.$t('navigation.dashboard'),
    };
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isLoggedIn', 'isRecruiter', 'isSuperAdmin' ]),
    dashboardBlocks() {
      return dashboardBlocks;
    },
  },
  methods: {
    filterJobsWhereUpdated(where) {
      this.filterJobsWhere = where;

      this.hasMoreJobsFilter = base64.encode(JSON.stringify({
        where: this.filterJobsWhere,
        order: this.filterJobsOrder,
      }));
      this.$scrollTo('.jobs-filter-container');
    },
    filterJobsOrderUpdated(order) {
      this.filterJobsOrder = order;

      this.hasMoreJobsFilter = base64.encode(JSON.stringify({
        where: this.filterJobsWhere,
        order: this.filterJobsOrder,
      }));
      this.$scrollTo('.jobs-filter-container');
    },
    async submitContactMentorRequest(form) {
      const { mentor, form: { message, selectedSkills: skills } } = form;
      const create = {
        message,
        skills,
        mentor: {
          id: mentor.id,
        },
      };
      try {
        await this.$apollo.mutate({
          mutation: MentorRequestCreateMutation,
          variables: { create },
          update: (store, { data: { createMentorRequest } }) => {
            const { sitePreferences: { defaultInternalJobOrderBy } = {} } = this.company;
            const data = store.readQuery({
              query: DashboardEmployeeQuery,
              variables: {
                jobOrder: defaultInternalJobOrderBy || 'MOST_RELEVANT_INTEREST',
                projectOrder: defaultInternalJobOrderBy || 'MOST_RELEVANT_INTEREST',
              },
            });
            if (data && data.user && Array.isArray(data.user.mentorRequests)) {
              data.user.mentorRequests.push(createMentorRequest);
            }
            store.writeQuery({ query: DashboardEmployeeQuery, data });
          },
        });
        Promise.resolve(this.$root.captureLog({
          eventName: 'IM_NEW_MENTOR_REQUEST',
          user: { id: this.user.id },
          data: [ { key: 'mentorID', value: mentor.id } ],
        }));
      } catch (error) {
        console.error(error);
      }
    },
    filterJobs(jobs) {
      const filteredJobs = (Array.isArray(jobs) ? jobs : []).filter(job => {
        const { imMatchingDetails } = job;
        if (!imMatchingDetails) return false;
        const { skillScore, interestScore } = imMatchingDetails;
        const score = interestScore + skillScore * 0.1;
        return score >= 2;
      });
      return filteredJobs;
    },
  },
};
</script>

<style lang="scss">
.internal-mobility-dashboard {
  h1 {
    font-size: 24px;
    font-weight: bold;
  }
  .block-title {
    text-transform: uppercase;
  }
  .upload-button {
    text-align: center;
    border-radius: 4px;
    &:hover {
      opacity: 0.8;
      cursor: pointer;
    }
    h3 {
      display: inline-block;
    }
    p {
      margin-block-start: 0;
      margin-block-end: 0;

    }
    &.card {
      padding: 0.5em;
      .card-body {
        padding: 0.5em;
      }
    }
  }

  .welcome-block {
    h1 {
      line-height: 1.28;
      font-weight: 400;
    }
    &.row {
      margin-left: 0px;
      margin-right: 0px
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Dashboards/InternalMobility.vue